import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import Head from 'next/head';
import { useRouter } from 'next/router';
import MyAccount from 'layouts/MyAccount';
import Overview from 'components/Overview';
import OverviewProfile from 'components/OverviewProfile';
import OverviewLinks from 'components/OverviewLinks';
import { Title } from 'components/OverviewProfile/styles';
import PlanDetailsCard from 'components/PlanDetailsCard';
import SubscriptionSuccededModal from 'components/SubscriptionSuccededModal';
import SubscriptionProcessingModal from 'components/SubscriptionProcessingModal';
import TeamInviteAcceptModal from 'components/TeamInviteAcceptModal';
import { UserTypeEnum } from 'enums';
import useIsMobile from 'hooks/useIsMobile';
import Cookies from 'js-cookie';
import { EditageConfig } from 'config/onelogin.config';
import { parseJwt, getOneCheckoutHostUrl } from 'utils';
import { useAuthUser } from 'features/auth/AuthUserContext';
import { withAuth } from 'features/auth/withAuth';
import getCleverTap from 'utils/clevertap';
import usePlan from 'hooks/usePlan';

function OverviewPage() {
  const { user, profile } = useAuthUser();
  const router = useRouter();
  const isMobile = useIsMobile();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showSubscriptionProcessingModal, setShowSubscriptionProcessingModal] = useState(false);
  const [showTeamInviteAcceptModal, setShowTeamInviteAcceptModal] = useState(false);
  const [plan] = usePlan();
  const isLabResearcherMember = user?.team !== 1 && user?.tp_user !== UserTypeEnum.LAB_RESEARCHER;
  const isTrial = router.query.trial === 'true';
  const userEditageToken = Cookies.get(EditageConfig.cookies.idCookieKey);
  const parsedUserEditageToken = userEditageToken ? parseJwt(userEditageToken) : {};
  const issUrl = parsedUserEditageToken.iss;
  const clevertap = getCleverTap();

  const TASKS_LINKS = [
    {
      external: false,
      href: plan
        ? '/my-account/billing/plan'
        : getOneCheckoutHostUrl(user.country_code) + '/payment-billing-history',
      label: 'Manage my billing information',
    },
    {
      ...(plan && {
        external: false,
        href: '/my-account/billing/available-plans',
        label: 'Upgrade/downgrade my plan',
      }),
    },
    {
      external: false,
      href: `https://${issUrl}/password/change?continue=${process.env.MTG_URL_APP}`,
      label: 'Change my password',
    },
    {
      external: true,
      href: 'https://mindthegraph.com/blog/faq/terms-of-use/',
      label: 'View privacy policy',
    },
  ];

  const RESOURCES_LINKS = [
    {
      external: true,
      href: 'https://blog.mindthegraph.com/category/tutorials/',
      label: 'Blog tutorials',
    },
    {
      external: true,
      href: 'https://www.youtube.com/mindthegraph',
      label: 'Video tutorials and tips',
    },
  ];

  function getTaskLinks() {
    if (isLabResearcherMember) {
      return TASKS_LINKS.filter((link) => {
        return !['Manage my billing information', 'Upgrade/downgrade my plan'].includes(link.label);
      });
    }

    return TASKS_LINKS;
  }

  async function closeSubscriptionSuccededModal() {
    if (isMobile) {
      router.push('/mobile-payment-finished');
    } else {
      setShowSubscriptionModal(false);

      window.history.pushState({}, document.title, window.location.pathname);
    }
  }

  function closeSubscriptionProcessingModal() {
    router.push('/pricing');
    /*if (isMobile) {
      router.push('/mobile-payment-finished');
    } else {
      setShowSubscriptionProcessingModal(false);

      window.history.pushState({}, document.title, window.location.pathname);
    }*/
  }

  function closeTeamInviteAcceptModal() {
    setShowTeamInviteAcceptModal(false);

    window.history.pushState({}, document.title, window.location.pathname);
  }

  function commonClevertapEvent() {
    return {
      Product: 'MTG',
      Email: user.email,
      'Country code': user.country_code,
      URL: router.asPath,
    };
  }

  useEffect(() => {
    if (router.query.status === 'success') {
      setShowSubscriptionModal(true);

      if (!isTrial) {
        ReactGA.event({
          category: 'checkout',
          action: 'order_finished',
          label: 'It opens the subscription confirmation modal',
          value: user.id,
        });
      }

      clevertap.event.push('Section load', {
        Description: 'Payment confirmation',
        ...commonClevertapEvent(),
      });
    }

    if (router.query.status === 'subscription_failed') {
      setShowSubscriptionProcessingModal(true);

      clevertap.event.push('Section load', {
        Description: 'Payment failed',
        ...commonClevertapEvent(),
      });
    }

    if (router.query.teamInviteAccept === '1') {
      setShowTeamInviteAcceptModal(true);
    }
  }, [router.query]);

  return (
    <MyAccount>
      <Head>
        <title>Overview - My Account - Mind The Graph</title>
      </Head>

      <SubscriptionSuccededModal
        disableActionButton={isMobile}
        open={showSubscriptionModal}
        onClose={closeSubscriptionSuccededModal}
      />

      <SubscriptionProcessingModal
        open={showSubscriptionProcessingModal}
        onClose={closeSubscriptionProcessingModal}
      />

      <TeamInviteAcceptModal
        token={router.query.token}
        open={showTeamInviteAcceptModal}
        onClose={closeTeamInviteAcceptModal}
      />

      {profile.idUser && (
        <Overview
          left={<OverviewProfile />}
          right={
            <>
              <Title>Current Plan</Title>

              <PlanDetailsCard />

              <OverviewLinks tasksLinks={getTaskLinks()} resourcesLinks={RESOURCES_LINKS} />
            </>
          }
        />
      )}
    </MyAccount>
  );
}

export default withAuth(OverviewPage);
